import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';
import { Avatar, Box, Chip, Divider, Drawer, IconButton, OutlinedInput, Tooltip, Typography } from '@mui/material';
import { IconArrowUp, IconRefresh, IconSparkles, IconX } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';

import api from '../../../views/services/api';
import useCurrentModule from '../../../hooks/useCurrentModule';

const SUGESTOES = ['Onde eu configuro os pipelines?', 'Como crio um campo personalizado?', 'Explique o que essa tela faz'];

// Paleta futurista própria do painel - deliberadamente independente das
// cores do resto do sistema (pedido explícito), só respeitando dark/light.
const getPalette = (isDark) =>
    isDark
        ? {
              bg: 'radial-gradient(circle at 15% 0%, rgba(99,102,241,0.18), transparent 45%), radial-gradient(circle at 100% 20%, rgba(6,182,212,0.12), transparent 40%), #0b0e1a',
              headerBorder: 'rgba(148,163,255,0.16)',
              accentGradient: 'linear-gradient(135deg, #6366f1 0%, #a855f7 55%, #06b6d4 100%)',
              userBubble: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              userText: '#ffffff',
              assistantBubble: 'rgba(255,255,255,0.045)',
              assistantBorder: '1px solid rgba(148,163,255,0.14)',
              assistantText: 'rgba(235,238,255,0.92)',
              textSecondary: 'rgba(210,215,255,0.5)',
              inputBg: 'rgba(255,255,255,0.04)',
              inputBorder: 'rgba(148,163,255,0.18)',
              chipBg: 'rgba(148,163,255,0.08)',
              chipBorder: 'rgba(148,163,255,0.25)',
              scrollThumb: 'rgba(148,163,255,0.25)',
              codeBg: 'rgba(0,0,0,0.35)'
          }
        : {
              bg: 'radial-gradient(circle at 15% 0%, rgba(99,102,241,0.10), transparent 45%), radial-gradient(circle at 100% 20%, rgba(6,182,212,0.08), transparent 40%), #f7f8fd',
              headerBorder: 'rgba(99,102,241,0.14)',
              accentGradient: 'linear-gradient(135deg, #6366f1 0%, #a855f7 55%, #06b6d4 100%)',
              userBubble: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              userText: '#ffffff',
              assistantBubble: '#ffffff',
              assistantBorder: '1px solid rgba(15,23,42,0.08)',
              assistantText: 'rgba(15,23,42,0.88)',
              textSecondary: 'rgba(15,23,42,0.5)',
              inputBg: '#ffffff',
              inputBorder: 'rgba(15,23,42,0.12)',
              chipBg: 'rgba(99,102,241,0.06)',
              chipBorder: 'rgba(99,102,241,0.25)',
              scrollThumb: 'rgba(99,102,241,0.25)',
              codeBg: 'rgba(15,23,42,0.06)'
          };

// Estilo dos elementos que o ReactMarkdown gera (p/strong/ul/li/code/a) -
// o Markdown renderiza tags HTML puras, então o texto de dentro precisa do
// próprio estilo tipográfico do bubble aplicado via seletor aninhado.
const markdownSx = (color) => ({
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color,
    '& p': { margin: 0, '&:not(:last-child)': { mb: 1 } },
    '& strong': { fontWeight: 700 },
    '& em': { fontStyle: 'italic' },
    '& ul, & ol': { margin: 0, paddingLeft: '1.2em', '&:not(:last-child)': { mb: 1 } },
    '& li': { mb: 0.3 },
    '& a': { color: 'inherit', fontWeight: 600, textDecorationColor: 'currentColor' },
    '& code': {
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '0.8em',
        padding: '2px 5px',
        borderRadius: '5px'
    },
    '& pre': {
        overflowX: 'auto',
        padding: '10px 12px',
        borderRadius: '10px',
        '& code': { padding: 0, background: 'none' }
    }
});

const TypingDots = ({ color }) => (
    <Box sx={{ display: 'flex', gap: '5px', px: 0.5, py: 0.5 }}>
        {[0, 1, 2].map((i) => (
            <Box
                key={i}
                sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: color,
                    '@keyframes bounceDot': {
                        '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
                        '30%': { transform: 'translateY(-5px)', opacity: 1 }
                    },
                    animation: 'bounceDot 1.2s ease-in-out infinite',
                    animationDelay: `${i * 0.15}s`
                }}
            />
        ))}
    </Box>
);

// Painel do Assistente IA contextual. Em telas md+ renderiza como uma
// caixa no fluxo normal do layout (mesma altura da caixa de conteúdo
// principal, ao lado dela - ver `MainLayout/index.js::copilotoBox`), não
// como um Drawer flutuando por cima de tudo. Em telas pequenas cai pra um
// Drawer `temporary` (overlay), já que não tem espaço pra lado a lado.
const CopilotoPanel = ({ open, onClose, boxClassName }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const palette = getPalette(isDark);
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const user = useSelector((state) => state.account.user);
    const navigationContext = useCurrentModule();

    const [appId, setAppId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [sending, setSending] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [text, setText] = useState('');
    const [error, setError] = useState('');
    const [focused, setFocused] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const listRef = useRef(null);
    const suggestionsTimerRef = useRef(null);

    useEffect(() => () => clearTimeout(suggestionsTimerRef.current), []);

    useEffect(() => {
        if (!open || appId) return;
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data?.app?.id || null);
            } catch (err) {
                console.error(err);
                setError('Erro ao carregar o Assistente IA.');
            }
        })();
    }, [open, appId]);

    useEffect(() => {
        if (!open || !appId) return;
        setLoadingHistory(true);
        api.get(`v1/apps/${appId}/copiloto/messages/`)
            .then((res) => setMessages(res.data?.messages || []))
            .catch((err) => console.error(err))
            .finally(() => setLoadingHistory(false));
    }, [open, appId]);

    useEffect(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages, sending, showSuggestions]);

    const sendText = async (value) => {
        const trimmed = value.trim();
        if (!trimmed || sending || !appId) return;

        clearTimeout(suggestionsTimerRef.current);
        setShowSuggestions(false);
        setSending(true);
        setError('');
        setText('');
        setMessages((prev) => [...prev, { role: 'user', sender_name: user?.first_name, text: trimmed }]);

        try {
            const res = await api.post(`v1/apps/${appId}/copiloto/messages/`, {
                text: trimmed,
                navigation: navigationContext
            });
            setMessages((prev) => [...prev, { role: 'system', sender_name: 'Assistente', text: res.data?.response }]);
            // Sugestões reaparecem sozinhas, um pouco depois da resposta,
            // pra dar continuidade sem precisar reabrir o painel.
            suggestionsTimerRef.current = setTimeout(() => setShowSuggestions(true), 1800);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'Erro ao falar com o Assistente IA.');
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendText(text);
        }
    };

    const handleReset = async () => {
        if (resetting || !appId) return;
        if (messages.length > 0 && !window.confirm('Reiniciar a conversa? Todo o histórico com o Assistente será apagado.')) {
            return;
        }

        clearTimeout(suggestionsTimerRef.current);
        setResetting(true);
        setError('');
        try {
            await api.delete(`v1/apps/${appId}/copiloto/messages/`);
            setMessages([]);
            setShowSuggestions(true);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'Erro ao reiniciar a conversa.');
        } finally {
            setResetting(false);
        }
    };

    const scrollbarSx = {
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { background: palette.scrollThumb, borderRadius: 10 },
        '&::-webkit-scrollbar-track': { background: 'transparent' }
    };

    const suggestionChips = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1 }}>
            {SUGESTOES.map((s) => (
                <Chip
                    key={s}
                    label={s}
                    onClick={() => sendText(s)}
                    sx={{
                        justifyContent: 'flex-start',
                        background: palette.chipBg,
                        border: `1px solid ${palette.chipBorder}`,
                        color: isDark ? '#e2e4ff' : '#312e81',
                        fontWeight: 500,
                        py: 2,
                        transition: 'all .2s ease',
                        '&:hover': { background: palette.accentGradient, color: '#fff', transform: 'translateY(-1px)' }
                    }}
                />
            ))}
        </Box>
    );

    const chatBody = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: palette.bg, position: 'relative' }}>
            {/* HEADER */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    px: 2,
                    py: 1.75,
                    borderBottom: `1px solid ${palette.headerBorder}`,
                    position: 'relative',
                    zIndex: 1
                }}
            >
                <Box
                    sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: palette.accentGradient,
                        boxShadow: isDark ? '0 0 18px rgba(99,102,241,0.55)' : '0 4px 14px rgba(99,102,241,0.35)',
                        '@keyframes spinSlow': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                        '& svg': { animation: 'spinSlow 6s linear infinite' }
                    }}
                >
                    <IconSparkles size={18} color="#fff" />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: '0.95rem',
                            letterSpacing: '0.02em',
                            background: palette.accentGradient,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}
                    >
                        LOGED IA
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: '#22d3ee',
                                boxShadow: '0 0 6px #22d3ee',
                                '@keyframes pulseDot': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
                                animation: 'pulseDot 1.8s ease-in-out infinite'
                            }}
                        />
                        <Typography sx={{ fontSize: '0.7rem', color: palette.textSecondary }}>ativo</Typography>
                    </Box>
                </Box>
                <Tooltip title="Reiniciar conversa">
                    <span>
                        <IconButton
                            onClick={handleReset}
                            disabled={resetting || !appId}
                            size="small"
                            sx={{
                                color: palette.textSecondary,
                                transition: 'transform .4s ease',
                                '&:hover': { color: isDark ? '#e2e4ff' : '#312e81', transform: 'rotate(-90deg)' }
                            }}
                        >
                            <IconRefresh size={17} />
                        </IconButton>
                    </span>
                </Tooltip>
                <IconButton onClick={onClose} size="small" sx={{ color: palette.textSecondary }}>
                    <IconX size={18} />
                </IconButton>
            </Box>

            {/* MENSAGENS */}
            <Box
                ref={listRef}
                sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, ...scrollbarSx }}
            >
                {loadingHistory && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <TypingDots color={palette.textSecondary} />
                    </Box>
                )}

                {!loadingHistory && messages.length === 0 && (
                    <Box
                        sx={{
                            mt: 2,
                            textAlign: 'center',
                            '@keyframes floatIn': {
                                from: { opacity: 0, transform: 'translateY(8px)' },
                                to: { opacity: 1, transform: 'translateY(0)' }
                            },
                            animation: 'floatIn 0.4s ease-out'
                        }}
                    >
                        <Box
                            sx={{
                                width: 52,
                                height: 52,
                                mx: 'auto',
                                mb: 1.5,
                                borderRadius: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: palette.accentGradient,
                                boxShadow: isDark ? '0 0 24px rgba(99,102,241,0.45)' : '0 6px 20px rgba(99,102,241,0.3)'
                            }}
                        >
                            <IconSparkles size={26} color="#fff" />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>Como posso ajudar?</Typography>
                        <Typography variant="body2" sx={{ color: palette.textSecondary, mb: 2, px: 1 }}>
                            Pergunte sobre configurações, funcionalidades ou peça ajuda com o que estiver vendo na tela.
                        </Typography>
                        {suggestionChips}
                    </Box>
                )}

                {messages.map((m, i) => (
                    <Box
                        key={i}
                        sx={{
                            alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                            maxWidth: '88%',
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-end',
                            '@keyframes msgIn': {
                                from: { opacity: 0, transform: 'translateY(6px) scale(0.98)' },
                                to: { opacity: 1, transform: 'translateY(0) scale(1)' }
                            },
                            animation: 'msgIn 0.25s ease-out'
                        }}
                    >
                        {m.role !== 'user' && (
                            <Avatar sx={{ width: 22, height: 22, background: palette.accentGradient, flexShrink: 0 }}>
                                <IconSparkles size={12} color="#fff" />
                            </Avatar>
                        )}
                        <Box
                            sx={{
                                borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                px: 1.75,
                                py: 1.1,
                                background: m.role === 'user' ? palette.userBubble : palette.assistantBubble,
                                border: m.role === 'user' ? 'none' : palette.assistantBorder,
                                boxShadow: m.role === 'user' ? '0 4px 14px rgba(99,102,241,0.28)' : 'none',
                                backdropFilter: m.role === 'user' ? 'none' : 'blur(6px)'
                            }}
                        >
                            <Box sx={markdownSx(m.role === 'user' ? palette.userText : palette.assistantText)}>
                                <ReactMarkdown
                                    components={{
                                        code: ({ inline, ...props }) => (
                                            <Box component="code" sx={{ background: inline ? palette.codeBg : 'transparent' }} {...props} />
                                        ),
                                        pre: (props) => <Box component="pre" sx={{ background: palette.codeBg }} {...props} />
                                    }}
                                >
                                    {m.text || ''}
                                </ReactMarkdown>
                            </Box>
                        </Box>
                    </Box>
                ))}

                {sending && (
                    <Box sx={{ alignSelf: 'flex-start', display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Avatar sx={{ width: 22, height: 22, background: palette.accentGradient }}>
                            <IconSparkles size={12} color="#fff" />
                        </Avatar>
                        <Box
                            sx={{
                                borderRadius: '16px 16px 16px 4px',
                                background: palette.assistantBubble,
                                border: palette.assistantBorder
                            }}
                        >
                            <TypingDots color={isDark ? '#a5b4fc' : '#6366f1'} />
                        </Box>
                    </Box>
                )}

                {!sending && showSuggestions && messages.length > 0 && (
                    <Box
                        sx={{
                            mt: 0.5,
                            '@keyframes floatIn': {
                                from: { opacity: 0, transform: 'translateY(6px)' },
                                to: { opacity: 1, transform: 'translateY(0)' }
                            },
                            animation: 'floatIn 0.35s ease-out'
                        }}
                    >
                        <Divider sx={{ borderColor: palette.headerBorder, mb: 1.5 }} />
                        {suggestionChips}
                    </Box>
                )}
            </Box>

            {error && (
                <Typography variant="caption" sx={{ px: 2, pb: 1, color: '#f87171' }}>
                    {error}
                </Typography>
            )}

            {/* INPUT */}
            <Box sx={{ p: 1.5, borderTop: `1px solid ${palette.headerBorder}`, position: 'relative', zIndex: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 1,
                        borderRadius: '18px',
                        background: palette.inputBg,
                        border: `1px solid ${focused ? 'transparent' : palette.inputBorder}`,
                        backgroundImage: focused
                            ? `linear-gradient(${palette.inputBg}, ${palette.inputBg}), ${palette.accentGradient}`
                            : 'none',
                        backgroundOrigin: 'border-box',
                        backgroundClip: 'padding-box, border-box',
                        boxShadow: focused ? (isDark ? '0 0 0 3px rgba(99,102,241,0.18)' : '0 0 0 3px rgba(99,102,241,0.12)') : 'none',
                        transition: 'box-shadow .2s ease',
                        p: 0.5,
                        pl: 1.5
                    }}
                >
                    <OutlinedInput
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder="Pergunte ao Assistente..."
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        disabled={sending || !appId}
                        sx={{
                            '& fieldset': { border: 'none' },
                            fontSize: '0.875rem',
                            color: isDark ? '#e5e7ff' : 'inherit'
                        }}
                    />
                    <IconButton
                        onClick={() => sendText(text)}
                        disabled={sending || !text.trim() || !appId}
                        sx={{
                            width: 34,
                            height: 34,
                            mb: 0.5,
                            background: text.trim() ? palette.accentGradient : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)',
                            color: text.trim() ? '#fff' : palette.textSecondary,
                            transition: 'transform .15s ease',
                            '&:hover': {
                                transform: text.trim() ? 'scale(1.08)' : 'none',
                                background: text.trim() ? palette.accentGradient : undefined
                            }
                        }}
                    >
                        <IconArrowUp size={18} />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );

    if (matchUpMd) {
        if (!open) return null;
        return <Box className={boxClassName}>{chatBody}</Box>;
    }

    return (
        <Drawer
            anchor="right"
            variant="temporary"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: '100%', display: 'flex', flexDirection: 'column' } }}
        >
            {chatBody}
        </Drawer>
    );
};

export default CopilotoPanel;
